// @owners { team: patients }
import { Column } from '@alto/design-system';
import React from 'react';
import { type UseFormStateReturn, useController, useFormContext } from 'react-hook-form';
import { trim } from 'validator';
import { RELATIONSHIP_CODES } from '~shared/constants';
import { type Values } from '~shared/features/insurances/helpers/getInitialInsuranceFormValues';
import {
  InsuranceBinInput,
  InsuranceGroupInput,
  InsuranceMemberIdInput,
  InsuranceNicknameInput,
  InsurancePcnInput,
  InsuranceRelationshipInputSelect,
} from '../../onboarding';

const validateBin = (value: string) => {
  if (!value) {
    return 'Rx BIN cannot be blank';
  }
  if (!/^\d+$/.test(value.trim())) {
    return 'Rx BIN must contain only numbers';
  }
  if (value.trim().length !== 6) {
    return 'Rx BIN must be exactly 6 digits';
  }
  if (new Set(value.split('')).size === 1) {
    return 'Rx BIN cannot have all digits be the same value';
  }
  return true;
};

export const InsuranceFormFields = () => {
  // React Compiler must not memoize this component: react-hook-form's useController/watch return
  // values driven by internal subscriptions and mutable refs (not React state), so memoizing them
  // captures stale field values and breaks validation/submit. `watch('relationship_code')` doesn't
  // trip the compiler's incompatible-library detector, so we opt out explicitly.
  'use no memo';

  // render controllers once so the parent react-hook-form can handle validation in different contexts
  // such as native and web profile, web provider-referred onboarding, and web organic onboarding
  const { control, watch } = useFormContext();

  const memberIDController = useController({
    name: 'member_id',
    control,
    rules: {
      required: 'Member ID cannot be blank',
    },
  });

  const binController = useController({
    name: 'bin',
    control,
    rules: {
      required: 'BIN cannot be blank',
      validate: validateBin,
    },
  });

  const groupController = useController({
    name: 'group',
    control,
  });

  const pcnController = useController({
    name: 'pcn',
    control,
  });

  const relationshipCodeController = useController({
    name: 'relationship_code',
    control,
    rules: {
      required: 'Relationship cannot be blank',
    },
  });

  const relationshipCode = watch('relationship_code');

  const firstNameController = useController({
    name: 'first_name',
    control,
    rules: {
      validate: (value: string) => {
        if (relationshipCode !== RELATIONSHIP_CODES.SELF && trim(value) === '') {
          return 'First name cannot be blank';
        }
        return true;
      },
    },
  });

  const lastNameController = useController({
    name: 'last_name',
    control,
    rules: {
      validate: (value: string) => {
        if (relationshipCode !== RELATIONSHIP_CODES.SELF && trim(value) === '') {
          return 'Last name cannot be blank';
        }
        return true;
      },
    },
  });

  const nicknameController = useController({
    name: 'nickname',
    control,
  });

  const mapControllerToInput = (controller: {
    field: any;
    formState?: UseFormStateReturn<Values>;
    fieldState: any;
  }) => {
    return {
      error: controller.fieldState.error?.message,
      onBlur: controller.field.onBlur,
      onChange: controller.field.onChange,
      value: controller.field.value,
    };
  };

  return (
    <Column gap="SM">
      <InsuranceMemberIdInput {...mapControllerToInput(memberIDController)} />
      <InsuranceBinInput {...mapControllerToInput(binController)} />
      <InsuranceGroupInput {...mapControllerToInput(groupController)} />
      <InsurancePcnInput {...mapControllerToInput(pcnController)} />
      <InsuranceRelationshipInputSelect
        firstName={firstNameController.field.value}
        firstNameError={firstNameController.fieldState.error?.message}
        lastName={lastNameController.field.value}
        lastNameError={lastNameController.fieldState.error?.message}
        onChangeFirstName={firstNameController.field.onChange}
        onChangeLastName={lastNameController.field.onChange}
        onChangeRelationship={relationshipCodeController.field.onChange}
        relationshipCode={relationshipCodeController.field.value}
        relationshipCodeError={relationshipCodeController.fieldState.error?.message}
      />
      <InsuranceNicknameInput {...mapControllerToInput(nicknameController)} />
    </Column>
  );
};
